"use client";

import {
  AddressElement,
  LinkAuthenticationElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { PaymentEvents, trackEvent } from "@/analytics";
import {
  SinglePaymentPolicyFooter,
  SubscriptionPaymentPolicyFooter,
} from "../policiesfooter";
import {
  checkUserAccess,
  getComedianProduct,
  sendSubscriptionConfirmationEmail,
} from "./paymentUtils";
import {
  clearPaymentStorage as clearLocalStorage,
  getPaymentData,
} from "./paymentStorageUtils";
import { useRouter, useSearchParams } from "next/navigation";

import AddMeToTheGuestList from "./AddMeToTheGuestList";
import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import Loader from "../common/icons/Loader";
import Modal from "../common/Modal";
import { Session } from "@supabase/supabase-js";
import { SignInSignUpForm } from "@punchup/ui-common/components/signin";
import { SpecialRow } from "@/data/specials";
import { cn } from "../common/utils";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { isSubscriptionContent } from "@punchup/data/legacy";
import { logException } from "@/analytics/sentry";
import { usePaymentProcessor } from "./usePaymentProcessor";
import { useToast } from "@/shadcn/ui/use-toast";

export default function StripePayment({
  amount,
  comedian,
  special,
  user,
}: {
  amount: number;
  comedian: Comedian;
  special: SpecialRow;
  user: { email: string | undefined; id: string } | null;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();

  const [email, setEmail] = useState(user?.email ?? "");
  const [location, setLocation] = useState<{
    postalCode: string;
    countryCode: string;
  }>({ postalCode: "", countryCode: "" });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [canLoadPaymentButton, setCanLoadPaymentButton] = useState(false);
  const [isSubscribedToChannel, setIsSubscribedToChannel] = useState(false);
  const [paymentType, setPaymentType] = useState("card");
  const [requestedToFollowComedian, setRequestedToFollowComedian] =
    useState(true); // Defaulting to true here is is US-centric, i.e. opt-out. In the EU this should be false, as EU requires opt-in.
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [localPaymentIntentId, setLocalPaymentIntentId] = useState<
    string | null
  >(null);

  const subscriptionContent = isSubscriptionContent(special);

  const paymentIntentId = searchParams.get("payment_intent_id");
  const followComedianParam = searchParams.get("followComedian");
  const clientSecretParam = searchParams.get("clientSecret");
  const paymentMethodIdParam = searchParams.get("paymentMethodId");
  const subscriptionIdParam = searchParams.get("subscriptionId");

  const currentPath = window.location.href;
  const redirectUrl = currentPath.substring(0, currentPath.lastIndexOf("/"));

  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.punchup.live";
  const fullCurrentPath = currentPath.startsWith("http")
    ? currentPath
    : `${baseUrl}${currentPath.startsWith("/") ? "" : "/"}${currentPath}`;
  const fullRedirectUrl = redirectUrl.startsWith("http")
    ? redirectUrl
    : `${baseUrl}${redirectUrl.startsWith("/") ? "" : "/"}${redirectUrl}`;

  const supabase = useMemo(() => {
    return createClientComponentClient<Database>({});
  }, []);

  const clearPaymentStorage = () => {
    clearLocalStorage(setLocalPaymentIntentId);
  };

  const checkUserAlreadyHasAccess = async () => {
    return await checkUserAccess(session, comedian, special);
  };

  const sendSubConfirmationEmail = async (userId: string) => {
    await sendSubscriptionConfirmationEmail(userId, email, amount);
  };

  const getComedianProductWrapper = async (comedianId: string) => {
    return await getComedianProduct(supabase, comedianId);
  };

  const {
    processPayment,
    isProcessingPayment,
    paymentProcessAttempted,
    setPaymentProcessAttempted,
    setError: setProcessorError,
  } = usePaymentProcessor(
    supabase,
    special,
    comedian,
    email,
    fullRedirectUrl,
    fullCurrentPath,
    checkUserAlreadyHasAccess,
    clearPaymentStorage,
    sendSubConfirmationEmail,
    getComedianProductWrapper
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedPaymentIntentId =
        window.localStorage.getItem("paymentIntentId");
      const effectivePaymentIntentId = paymentIntentId || storedPaymentIntentId;

      if (
        effectivePaymentIntentId &&
        effectivePaymentIntentId !== "null" &&
        effectivePaymentIntentId !== "undefined"
      ) {
        if (effectivePaymentIntentId !== storedPaymentIntentId) {
          window.localStorage.setItem(
            "paymentIntentId",
            effectivePaymentIntentId
          );
        }

        if (effectivePaymentIntentId !== localPaymentIntentId) {
          setLocalPaymentIntentId(effectivePaymentIntentId);
        }

        if (paymentIntentId) {
          const url = new URL(window.location.href);
          url.searchParams.delete("payment_intent_id");
          url.searchParams.delete("followComedian");
          window.history.replaceState({}, document.title, url.toString());
        }
      }

      if (clientSecretParam) {
        window.localStorage.setItem("paymentClientSecret", clientSecretParam);
      }

      if (paymentMethodIdParam) {
        window.localStorage.setItem("paymentMethodId", paymentMethodIdParam);
      }

      if (subscriptionIdParam) {
        window.localStorage.setItem("subscriptionId", subscriptionIdParam);
      }

      if (clientSecretParam || paymentMethodIdParam || subscriptionIdParam) {
        const url = new URL(window.location.href);
        if (clientSecretParam) url.searchParams.delete("clientSecret");
        if (paymentMethodIdParam) url.searchParams.delete("paymentMethodId");
        if (subscriptionIdParam) url.searchParams.delete("subscriptionId");
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  }, [
    paymentIntentId,
    localPaymentIntentId,
    clientSecretParam,
    paymentMethodIdParam,
    subscriptionIdParam,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined" && getPaymentData().paymentCompleted) {
      if (window.location.href.includes("/checkout/")) {
        window.location.href = fullRedirectUrl;
      }
    }
  }, [fullRedirectUrl]);

  useEffect(() => {
    let isRunning = false;

    const checkSessionAndProcessPayment = async () => {
      if (
        isRunning ||
        isProcessingPayment ||
        paymentProcessAttempted ||
        (typeof window !== "undefined" && getPaymentData().paymentCompleted)
      ) {
        return;
      }

      isRunning = true;

      try {
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();

        if (JSON.stringify(session) !== JSON.stringify(currentSession)) {
          setSession(currentSession);
        }

        if (user && !paymentProcessAttempted && !isProcessingPayment) {
          const intentIdToProcess =
            localPaymentIntentId ||
            (paymentIntentId &&
            paymentIntentId !== "null" &&
            paymentIntentId !== "undefined"
              ? paymentIntentId
              : null) ||
            (typeof window !== "undefined"
              ? window.localStorage.getItem("paymentIntentId")
              : null);

          const clientSecret =
            typeof window !== "undefined"
              ? window.localStorage.getItem("paymentClientSecret") ||
                clientSecretParam
              : clientSecretParam;

          const paymentMethodId =
            typeof window !== "undefined"
              ? window.localStorage.getItem("paymentMethodId") ||
                paymentMethodIdParam
              : paymentMethodIdParam;

          if (intentIdToProcess) {
            setPaymentProcessAttempted(true);
            processPayment(
              user.id,
              intentIdToProcess,
              location,
              followComedianParam === "true" || requestedToFollowComedian
            );
          } else if (clientSecret && paymentMethodId) {
            setPaymentProcessAttempted(true);
            processPayment(
              user.id,
              paymentMethodId,
              location,
              followComedianParam === "true" || requestedToFollowComedian
            );
          }
        }
      } catch (error) {
        console.error("Error checking session or processing payment:", error);
      } finally {
        isRunning = false;
      }
    };

    if (typeof window === "undefined" || !getPaymentData().paymentCompleted) {
      checkSessionAndProcessPayment();
    }
  }, [
    supabase,
    paymentIntentId,
    localPaymentIntentId,
    clientSecretParam,
    paymentMethodIdParam,
    paymentProcessAttempted,
    isProcessingPayment,
    session,
    processPayment,
    location,
    followComedianParam,
    requestedToFollowComedian,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const currentContentId = special.id;
      const lastContentId = window.localStorage.getItem(
        "last_purchased_content"
      );

      if (currentContentId !== lastContentId) {
        window.localStorage.removeItem("payment_completed");
        window.localStorage.setItem("last_purchased_content", currentContentId);
      }
    }
  }, [special.id]);

  const handleAddressChange = (e: any) => {
    const postalCode = e.value.address.postal_code;
    const countryCode = e.value.address.country;
    setLocation({ postalCode, countryCode });
  };

  useEffect(() => {
    if (error.length) {
      toast({
        title: error,
        variant: "destructive",
      });
      logException(`Stripe Payment failed with Error: ${error}`);
      trackEvent(
        PaymentEvents.FAILED_PAYMENT,
        { error },
        { send_immediately: true }
      );
    }
  }, [error, toast]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    if (typeof window !== "undefined" && getPaymentData().paymentCompleted) {
      if (window.location.href.includes("/checkout/")) {
        window.location.href = fullRedirectUrl;
      }
      return;
    }

    setIsLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message ?? "Error submitting payment form");
        setIsLoading(false);
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (user) {
        const hasAccess = await checkUserAlreadyHasAccess();
        if (hasAccess) {
          toast({
            title: "You already have access to this content",
            variant: "success",
          });

          if (typeof window !== "undefined") {
            window.localStorage.setItem("payment_completed", "true");
            window.localStorage.setItem("last_purchased_content", special.id);
            window.location.href = redirectUrl;
          }
          setIsLoading(false);
          return;
        }
      }

      let clientSecret;
      let stripeSubscriptionID;

      if (subscriptionContent) {
        const customerResponse = await fetch(
          "/api/stripe/createStripeCustomer",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: email }),
          }
        );

        const customerData = await customerResponse.json();
        const stripeCustomerID = customerData.stripeCustomerID;

        const comedianProduct = await getComedianProductWrapper(comedian.id);
        const { price_id: priceID, id: productID } = comedianProduct[0];

        const stripeCheckoutResponse = await fetch(
          "/api/stripe/stripeSubscriptionSetup",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              special: special.slug,
              priceID,
              stripeCustomerID,
              comedianID: comedian.id,
              subscription: true,
              productID,
              email,
              confirm: false, // Don't confirm yet
            }),
          }
        );

        const responseData = await stripeCheckoutResponse.json();
        clientSecret = responseData.clientSecret;
        stripeSubscriptionID = responseData.subscriptionId;
      } else {
        const stripeCheckoutResponse = await fetch(
          "/api/stripe/payment-setup",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              special: special.slug,
              comedianID: comedian.id,
              subscription: false,
              email,
              confirm: false,
            }),
          }
        );

        const responseData = await stripeCheckoutResponse.json();
        clientSecret = responseData.clientSecret;
      }

      if (!clientSecret) {
        setError("Failed to set up payment");
        setIsLoading(false);
        return;
      }

      if (session?.user) {
        try {
          const paymentResult = await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams: {
              return_url: fullRedirectUrl,
              payment_method_data: {
                billing_details: { email },
              },
            },
            redirect: "if_required",
          });

          if (paymentResult.error) {
            console.error("Error confirming payment:", paymentResult.error);
            setError(paymentResult.error.message ?? "Error processing payment");
            setIsLoading(false);
            return;
          }

          if (paymentResult.paymentIntent) {
            await processPayment(
              session.user.id,
              paymentResult.paymentIntent.id,
              location,
              requestedToFollowComedian
            );
            setIsLoading(false);
          }
        } catch (confirmError: any) {
          console.error("Exception during payment confirmation:", confirmError);
          setError(confirmError.message ?? "Error processing payment");
          setIsLoading(false);
        }

        return;
      } else {
        try {
          const { error: collectError, paymentMethod } =
            await stripe.createPaymentMethod({
              elements,
              params: {
                billing_details: { email },
              },
            });

          if (collectError) {
            setError(
              collectError.message ?? "Error collecting payment details"
            );
            setIsLoading(false);
            return;
          }

          if (paymentMethod) {
            if (typeof window !== "undefined") {
              window.localStorage.setItem("paymentClientSecret", clientSecret);
              window.localStorage.setItem("paymentMethodId", paymentMethod.id);
              window.localStorage.setItem("paymentEmail", email);
              if (stripeSubscriptionID) {
                window.localStorage.setItem(
                  "subscriptionId",
                  stripeSubscriptionID
                );
              }

              window.localStorage.setItem("auth_in_progress", "true");
            }
            setSignInModalOpen(true);
            setIsLoading(false);
            return;
          }
        } catch (stripeError: any) {
          console.error("Stripe error creating payment method:", stripeError);
          setError(
            stripeError.message ?? "Error processing payment information"
          );
          setIsLoading(false);
          return;
        }
      }

      return;
    } catch (error) {
      console.error("Error during payment setup:", error);
      setError("Error processing payment");
      setIsLoading(false);
    }
  };

  const handlePaymentTypeChange = (e: any) => {
    setPaymentType(e.value.type);
  };

  const paymentElementOptions = {
    business: {
      name: "Punchup",
    },
    layout: "tabs",
    loader: "always",
    terms: {
      applePay: "never",
      card: "never",
      cashapp: "never",
      googlepay: "never",
    },
  };

  const addressElementOptions = { mode: "billing", loader: "always" };
  const linkAuthenticationOptions = {
    defaultValues: {
      email: email,
    },
    disabled: !!user,
  };

  const shouldCollectAddress = Boolean(paymentType === "card");

  const buildRedirectUrlWithPaymentParams = () => {
    let redirectUrl = `${fullCurrentPath}?followComedian=${requestedToFollowComedian}`;

    if (typeof window !== "undefined") {
      const clientSecret = window.localStorage.getItem("paymentClientSecret");
      const paymentMethodId = window.localStorage.getItem("paymentMethodId");
      const subscriptionId = window.localStorage.getItem("subscriptionId");

      if (clientSecret) {
        redirectUrl += `&clientSecret=${encodeURIComponent(clientSecret)}`;
      }

      if (paymentMethodId) {
        redirectUrl += `&paymentMethodId=${encodeURIComponent(paymentMethodId)}`;
      }

      if (subscriptionId) {
        redirectUrl += `&subscriptionId=${encodeURIComponent(subscriptionId)}`;
      }
    }

    return redirectUrl;
  };

  return (
    <div className={cn("w-full", isLoading && "pointer-events-none")}>
      <form id="payment-form" onSubmit={handleSubmit} className="px-4">
        {!user && (
          <div className="mb-6">
            <p className="text-gray-600">
              Already have an account?{" "}
              <a
                href={`/signin?redirectRoute=${encodeURIComponent(currentPath)}`}
                className="text-[#ff5e26] hover:text-[#FF6E3C] underline"
              >
                Log in
              </a>
            </p>
          </div>
        )}

        <LinkAuthenticationElement
          id="link-authentication-element"
          className={cn("mb-2", user?.email && "hidden")}
          options={linkAuthenticationOptions as any}
          onChange={(e: any) => {
            setEmail(e.value.email);
          }}
        />
        {user?.email && (
          <div className="mb-2 p-Field">
            <label
              className="p-FieldLabel Label"
              style={{
                color: "rgb(48, 49, 61)",
                fontSize: "0.93rem",
                fontWeight: 400,
                fontFamily: `-apple-system, "system-ui", "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif`,
              }}
            >
              Email
            </label>
            <p className="text-sm text-gray-500 py-2">{user.email}</p>
          </div>
        )}

        {shouldCollectAddress && (
          <AddressElement
            onChange={handleAddressChange}
            options={addressElementOptions as any}
            className="mb-2"
          />
        )}
        <PaymentElement
          id="payment-element"
          onReady={() => {
            setCanLoadPaymentButton(true);
          }}
          onChange={handlePaymentTypeChange}
          options={paymentElementOptions as any}
        />
        {subscriptionContent && (
          <SubscriptionPaymentPolicyFooter
            paymentType={paymentType}
            amount={(amount / 100).toFixed(2).toString()}
          />
        )}

        {!subscriptionContent && canLoadPaymentButton && (
          <AddMeToTheGuestList
            comedianShortName={comedian.display_name_short}
            comedianId={comedian.id}
            followComedian={requestedToFollowComedian}
            setFollowComedian={setRequestedToFollowComedian}
          />
        )}
        {!subscriptionContent && canLoadPaymentButton && (
          <SinglePaymentPolicyFooter />
        )}
        {canLoadPaymentButton && (
          <>
            <button
              type="submit"
              className={cn(
                "w-full bg-[#ff5e26]  hover:bg-[#FF6E3C] text-white font-medium py-2 my-5 rounded-md transition-colors",
                isLoading && "opacity-50"
              )}
              disabled={
                isLoading || !stripe || !elements || isProcessingPayment
              }
              id="submit"
            >
              <span id="button-text">
                {subscriptionContent ? "Subscribe" : "Pay now"}
              </span>
            </button>
          </>
        )}
      </form>

      {signInModalOpen && (
        <Modal
          onClose={() => {
            setSignInModalOpen(false);
          }}
        >
          <div className="sm:mx-auto sm:w-full max-w-[80vw] sm:max-w-sm">
            <h2 className="mt-6 text-2xl font-bold leading-9 tracking-tight text-center text-gray-900">
              Sign in to access your content
            </h2>
            <div className="mt-4">
              <SignInSignUpForm
                buttonText="Continue"
                redirectTo={buildRedirectUrlWithPaymentParams()}
                userEmail={email}
              />
            </div>
          </div>
        </Modal>
      )}

      {isProcessingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-md flex flex-col items-center gap-2">
            <Loader width={16} height={16} />
            <p>Processing payment...</p>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-md flex flex-col items-center gap-2">
            <Loader width={16} height={16} />
            <p>Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
}
